import { flow, types } from 'mobx-state-tree';

import {
  dataEntryClerks,
  fieldInspectors,
  inspectionList,
  serviceProviders,
  siteDisciplineList,
  workPackageFiles,
  workPackageList,
} from '../../graphql';
import { sentryCaptureException } from '../../utilities';
import { ApiStatus } from '../../utilities/enums';

const PrimaryContextRecord = types.model({
  siteId: types.number,
  siteName: types.string,
  inspectorId: types.number,
  clerkId: types.number,
  dateModified: types.string,
  disciplines: types.array(
    types.model({
      disciplineId: types.number,
      disciplineName: types.string,
      totalSchedules: types.number,
    }),
  ),
});

const WorkPackageRecord = types.model({
  workPackageId: types.string,
  workPackageStatusId: types.number,
  noOfInspections: types.number,
});

const InspectionListRecord = types.model({
  inspectionId: types.string,
  workPackageId: types.number,
});

const serviceProviderRecord = types.model({
  pkServiceProvider: types.number,
  organizationName: types.string,
});
const fieldInspectorListRecord = types.model({
  fkServiceProvider: types.maybeNull(types.number),
  pkInspector: types.number,
  isLeadInspector: types.maybeNull(types.boolean),
  fkUser: types.maybeNull(types.number),
  isActive: types.maybeNull(types.boolean),
  companyName: types.maybeNull(types.string),
  title: types.maybeNull(types.string),
  firstName: types.maybeNull(types.string),
  lastName: types.maybeNull(types.string),
  phone: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  emailAddress: types.maybeNull(types.string),
  primaryAddress: types.maybeNull(types.string),
  abbrv: types.maybeNull(types.string),
  comment: types.maybeNull(types.string),
  dateModified: types.maybeNull(types.string),
  modifiedBy: types.maybeNull(types.string),
});
const dataEntryClerksListRecord = types.model({
  fkServiceProvider: types.maybeNull(types.number),
  clerkId: types.maybeNull(types.number),
  pkClerk: types.number,
  fkUser: types.maybeNull(types.number),
  isActive: types.maybeNull(types.boolean),
  title: types.maybeNull(types.string),
  companyName: types.maybeNull(types.string),
  firstName: types.maybeNull(types.string),
  lastName: types.maybeNull(types.string),
  phone: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  emailAddress: types.maybeNull(types.string),
  primaryAddress: types.maybeNull(types.string),
  abbrv: types.maybeNull(types.string),
  comment: types.maybeNull(types.string),
  dateModified: types.maybeNull(types.string),
  modifiedBy: types.maybeNull(types.string),
});

export const PrimaryContext = types
  .model({
    PrimaryContextMap: types.map(PrimaryContextRecord),
    PrimaryContextList: types.array(PrimaryContextRecord),
    WorkPackageList: types.array(WorkPackageRecord),
    InspectionList: types.array(InspectionListRecord),
    serviceProviderList: types.array(serviceProviderRecord),
    fieldInspectorList: types.array(fieldInspectorListRecord),
    dataEntryClerksList: types.array(dataEntryClerksListRecord),
    PrimaryContextListFetchStatus: types.enumeration('STATUS', [
      ApiStatus.NOT_CALLED,
      ApiStatus.LOADING,
      ApiStatus.ERROR,
      ApiStatus.SUCCESS,
    ]),
    WorkPackageListFetchStatus: types.enumeration('STATUS', [
      ApiStatus.NOT_CALLED,
      ApiStatus.LOADING,
      ApiStatus.ERROR,
      ApiStatus.SUCCESS,
    ]),
    InspectionListFetchStatus: types.enumeration('STATUS', [
      ApiStatus.NOT_CALLED,
      ApiStatus.LOADING,
      ApiStatus.ERROR,
      ApiStatus.SUCCESS,
    ]),
    wpFiles: types.array(
      types.model({
        fileId: types.string,
        workPackageId: types.string,
        siteId: types.number,
        disciplineId: types.number,
      }),
    ),
    wpFilesFetchStatus: types.enumeration('STATUS', [
      ApiStatus.NOT_CALLED,
      ApiStatus.LOADING,
      ApiStatus.ERROR,
      ApiStatus.SUCCESS,
    ]),
    sessionPrimaryContextId: types.maybe(types.string),
    sessionPrimaryContextName: types.maybe(types.string),
  })
  .views(self => ({
    get primaryContextList() {
      return self.PrimaryContextList.toJSON();
    },
    get primaryContextListFetchStatus() {
      return self.PrimaryContextListFetchStatus;
    },
  }))
  .actions(self => ({
    getSiteDisciplineList: flow(function* getSiteDisciplineList() {
      if (self.PrimaryContextListFetchStatus === ApiStatus.LOADING) {
        return;
      }
      self.PrimaryContextListFetchStatus = ApiStatus.LOADING;
      self.PrimaryContextMap.clear();
      try {
        const result = yield siteDisciplineList();

        if (result.error === undefined) {
          const inspectorSiteScheduleSummary = result.data.inspectorSiteScheduleSummary;
          self.PrimaryContextList = inspectorSiteScheduleSummary.length
            ? inspectorSiteScheduleSummary
            : [];
          self.PrimaryContextListFetchStatus = ApiStatus.SUCCESS;
        } else {
          self.PrimaryContextListFetchStatus = ApiStatus.ERROR;
        }
      } catch (error) {
        console.log('🚀 ~ file: primaryContext.ts ~ line 68 ~ error', error); // eslint-disable-line no-console
        // console logs will be removed once sentry implementation is done
        sentryCaptureException(
          error,
          '~ file: PrimaryContext.tsx ~ line 132 ~ getSiteDisciplineList ~',
        );
        self.PrimaryContextListFetchStatus = ApiStatus.ERROR;
      }
    }),
    getWorkPackageFromMobx: flow(function* getWorkPackageFromMobx(
      siteId: number,
      disciplineId: number,
    ) {
      try {
        if (self.WorkPackageListFetchStatus === ApiStatus.LOADING) {
          return;
        }
        self.WorkPackageListFetchStatus = ApiStatus.LOADING;
        const result = yield workPackageList(siteId, disciplineId);
        if (result?.errors) {
          throw result.errors;
        }
        const filteredWp = result.data.mobileWorkPackageFeed.filter(
          (e: { workPackageStatusId: number }) => e?.workPackageStatusId !== 4,
        );
        self.WorkPackageList = filteredWp;
        self.WorkPackageListFetchStatus = ApiStatus.SUCCESS;
      } catch (error) {
        self.WorkPackageListFetchStatus = ApiStatus.ERROR;
        console.log('Error ', error); // eslint-disable-line no-console
        // console logs will be removed once sentry implementation is done
        sentryCaptureException(
          error,
          '~ file: primaryContext.tsx ~ line 160 ~ getWorkPackageFromMobx ~',
        );
      }
    }),
    getInspectionListFromMobx: flow(function* getInspectionListFromMobx(
      siteId: number,
      disciplineId: number,
    ) {
      try {
        if (self.InspectionListFetchStatus === ApiStatus.LOADING) {
          return;
        }
        self.InspectionListFetchStatus = ApiStatus.LOADING;
        const result = yield inspectionList(siteId, disciplineId);
        if (result?.errors) {
          throw result.errors;
        }
        self.InspectionList = result.data.assetInspectionsFeed;
        self.InspectionListFetchStatus = ApiStatus.SUCCESS;
      } catch (error) {
        self.InspectionListFetchStatus = ApiStatus.ERROR;
        console.log('Error ', error); // eslint-disable-line no-console
        // console logs will be removed once sentry implementation is done
        sentryCaptureException(error, '~ file: primaryContext.tsx ~ line 180 ~ inspectionList ~');
      }
    }),
    getSPListFromMobx: flow(function* getSPListFromMobx() {
      try {
        const result = yield serviceProviders();
        self.serviceProviderList = result.data.allServiceProvidersInfo;
      } catch (error) {
        console.log('Error ', error); // eslint-disable-line no-console
        // console logs will be removed once sentry implementation is done
        sentryCaptureException(
          error,
          '~ file: primaryContext.tsx ~ line 190 ~ getSPListFromMobx ~',
        );
      }
    }),
    getFIListFromMobx: flow(function* getFIListFromMobx() {
      try {
        const result = yield fieldInspectors();
        self.fieldInspectorList = result.data.allInspectorsInfo;
      } catch (error) {
        console.log('Error ', error); // eslint-disable-line no-console
        // console logs will be removed once sentry implementation is done
        sentryCaptureException(
          error,
          '~ file: primaryContext.tsx ~ line 220 ~ getFIListFromMobx ~',
        );
        throw error;
      }
    }),
    getDataEntryClerksFromMobx: flow(function* getDataEntryClerksFromMobx() {
      try {
        const result = yield dataEntryClerks();
        self.dataEntryClerksList = result;
      } catch (error) {
        console.log('Error ', error); // eslint-disable-line no-console
        // console logs will be removed once sentry implementation is done
        sentryCaptureException(
          error,
          '~ file: primaryContext.tsx ~ line 229 ~ getDataEntryClerksFromMobx ~',
        );
        throw error;
      }
    }),

    resetStore: () => {
      self.PrimaryContextMap.clear();
      self.PrimaryContextList.clear();
      self.PrimaryContextListFetchStatus = ApiStatus.NOT_CALLED;
      self.sessionPrimaryContextId = undefined;
    },
    resetWorkPackageList: () => {
      self.WorkPackageList.clear();
      self.WorkPackageListFetchStatus = ApiStatus.NOT_CALLED;
    },
    resetInspectionList: () => {
      self.InspectionList.clear();
      self.InspectionListFetchStatus = ApiStatus.NOT_CALLED;
    },
    getwpFilesFromMobx: flow(function* getwpFilesFromMobx(siteId: number, disciplineId: number) {
      try {
        if (self.wpFilesFetchStatus === ApiStatus.LOADING) {
          return;
        }
        self.wpFilesFetchStatus = ApiStatus.LOADING;
        const result = yield workPackageFiles(siteId, disciplineId);
        if (result?.errors) {
          throw result.errors;
        }
        self.wpFiles = result.data.workPackageFileDetailsFeed;
        self.wpFilesFetchStatus = ApiStatus.SUCCESS;
      } catch (error) {
        self.wpFilesFetchStatus = ApiStatus.ERROR;
        console.log('~ file: primaryContext.tsx ~ line 230 ~ getwpFilesFromMobx ~ ', error); // eslint-disable-line no-console
        // console logs will be removed once sentry implementation is done
        sentryCaptureException(
          error,
          '~ file: primaryContext.tsx ~ line 230 ~ getwpFilesFromMobx ~',
        );
      }
    }),
    resetWpFiles: () => {
      self.wpFiles.clear();
      self.wpFilesFetchStatus = ApiStatus.NOT_CALLED;
    },
  }));
export default PrimaryContext;
